import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useClient } from 'shared/apollo';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import TrueImpactCheckbox from 'shared/design-system/components/TrueImpactCheckbox';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faScaleUnbalanced,
  faChevronDown,
  faChevronRight,
  faXmark,
} from '@fortawesome/pro-light-svg-icons';
import {
  OverAllocatedInvestmentItem,
  getOverAllocatedInvestments,
} from './api';

const PageWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: ${SPACERS.large};
  box-sizing: border-box;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACERS.small};
  margin-bottom: ${SPACERS.medium};
  .header-icon {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: ${COLORS.ctaPrimaryBackground};
    color: ${COLORS.ctaPrimary};
  }
  .heading {
    margin: 0;
  }
  .subheading {
    margin: 2px 0 0;
    color: ${COLORS.grayDark};
  }
`;

const Card = styled.div`
  background: ${COLORS.whiteBase};
  border: 1px solid ${COLORS.greyLight};
  border-radius: 12px;
  padding: ${SPACERS.medium};
  margin-bottom: ${SPACERS.medium};
  box-shadow: 0 2px 8px rgba(0, 115, 163, 0.05);
`;

const FiltersRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr auto;
  gap: ${SPACERS.small};
  align-items: end;
  margin-top: ${SPACERS.small};
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
  .field-label {
    display: block;
    margin-bottom: 4px;
    color: ${COLORS.grayDarkest};
  }
`;

const ChipsField = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid ${COLORS.greyBase};
  border-radius: 6px;
  min-height: 53.78px;
  box-sizing: border-box;
  background: ${COLORS.whiteBase};
  input {
    flex: 1;
    min-width: 140px;
    border: none;
    outline: none;
    font-size: 14px;
    background: transparent;
  }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: ${COLORS.ctaPrimaryBackground};
  color: ${COLORS.ctaPrimary};
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  .remove {
    cursor: pointer;
    display: inline-flex;
    &:hover {
      opacity: 0.7;
    }
  }
`;

const ErrorBanner = styled.div`
  background: ${COLORS.criticalBackground};
  color: ${COLORS.critical};
  border: 1px solid ${COLORS.critical};
  border-radius: 8px;
  padding: ${SPACERS.small} ${SPACERS.medium};
  margin-top: ${SPACERS.small};
  white-space: pre-wrap;
  word-break: break-word;
`;

const StyledTableContainer = styled(TableContainer)`
  border: 1px solid ${COLORS.greyLight};
  border-radius: 12px;
  background: ${COLORS.whiteBase};
  .mono {
    font-family: monospace;
    font-size: 12px;
  }
  .over {
    color: ${COLORS.critical};
    font-weight: 600;
  }
  .balanced {
    color: ${COLORS.success};
    font-weight: 600;
  }
  .under {
    color: ${COLORS.warningDark};
    font-weight: 600;
  }
`;

const NestedSection = styled.div`
  background: ${COLORS.greyLight};
  padding: ${SPACERS.small} ${SPACERS.medium} ${SPACERS.medium}
    ${SPACERS.medium};
  border-top: 1px solid ${COLORS.greyLight};
  .section-title {
    margin: ${SPACERS.tiny} 0;
    color: ${COLORS.grayDarkest};
    font-weight: 700;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${SPACERS.medium};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${SPACERS.extraLarge} ${SPACERS.large};
  color: ${COLORS.grayDark};
  background: ${COLORS.greyLightest};
  border-radius: 12px;
  border: 1px dashed ${COLORS.greyBase};
  .icon {
    font-size: 40px;
    color: ${COLORS.grayBase};
    margin-bottom: ${SPACERS.small};
  }
`;

const StatusPill = styled.span<{ tone: 'default' | 'warn' | 'ok' | 'muted' }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  background: ${({ tone }) =>
    tone === 'warn'
      ? COLORS.criticalBackground
      : tone === 'ok'
      ? COLORS.successBackground
      : tone === 'muted'
      ? COLORS.greyLightest
      : COLORS.ctaPrimaryBackground};
  color: ${({ tone }) =>
    tone === 'warn'
      ? COLORS.critical
      : tone === 'ok'
      ? COLORS.success
      : tone === 'muted'
      ? COLORS.grayDark
      : COLORS.ctaPrimary};
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatGqlError = (err: any): string => {
  if (!err) return 'Unknown error.';
  const gqlErrors = err.graphQLErrors || err?.networkError?.result?.errors;
  if (gqlErrors && gqlErrors.length) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return gqlErrors.map((e: any) => e.message).join('\n');
  }
  return err.message || String(err);
};

const toNumber = (s: string | null | undefined): number => {
  if (s === null || s === undefined || s === '') return 0;
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
};

const formatMoney = (s: string | null | undefined): string => {
  const n = toNumber(s);
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const statusTone = (
  status: string | null | undefined,
): 'default' | 'warn' | 'ok' | 'muted' => {
  if (!status) return 'muted';
  const s = status.toLowerCase();
  if (s === 'allocated') return 'ok';
  if (s === 'rejected' || s === 'returned') return 'warn';
  if (s === 'unnotified') return 'muted';
  return 'default';
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

interface RowProps {
  item: OverAllocatedInvestmentItem;
}

const InvestmentRow: React.FC<RowProps> = ({ item }) => {
  const [open, setOpen] = useState(false);
  const totalInvestment = toNumber(item.totalInvestment);
  const totalAllocated = toNumber(item.totalAllocated);
  const overBy = totalAllocated - totalInvestment;
  const allocationMultiple =
    totalInvestment > 0 ? totalAllocated / totalInvestment : 0;
  const isDoubleAllocated =
    totalInvestment > 0 &&
    allocationMultiple >= 2 &&
    Math.abs(allocationMultiple - Math.round(allocationMultiple)) < 0.0001;
  return (
    <>
      <TableRow
        hover
        style={
          isDoubleAllocated
            ? { backgroundColor: COLORS.ctaBrandBackground }
            : undefined
        }
      >
        <TableCell style={{ width: 44 }}>
          <IconButton size="small" onClick={() => setOpen(o => !o)}>
            <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
          </IconButton>
        </TableCell>
        <TableCell className="mono" align="right">
          {item.investmentId}
        </TableCell>
        <TableCell>{formatDate(item.createdAt)}</TableCell>
        <TableCell>{item.funderName || '—'}</TableCell>
        <TableCell>{item.funderEmail || '—'}</TableCell>
        <TableCell>
          {item.granteeAccountId === null ||
          item.granteeAccountId === undefined ? (
            <StatusPill tone="muted">No grantee</StatusPill>
          ) : (
            <span className="mono">{item.granteeAccountId}</span>
          )}
        </TableCell>
        <TableCell title={item.programName || ''}>
          {item.programName || (
            <em style={{ color: COLORS.grayDark }}>(none)</em>
          )}
        </TableCell>
        <TableCell align="right">{formatMoney(item.totalInvestment)}</TableCell>
        <TableCell
          align="right"
          className={overBy > 0 ? 'over' : overBy === 0 ? 'balanced' : 'under'}
        >
          {formatMoney(item.totalAllocated)}
        </TableCell>
        <TableCell
          align="right"
          className={overBy > 0 ? 'over' : overBy === 0 ? 'balanced' : 'under'}
        >
          {formatMoney(String(overBy))}
        </TableCell>
        <TableCell align="right">{item.allocationCount}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={11} style={{ padding: 0, borderBottom: 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <NestedSection>
              <TrueImpactText
                className="section-title"
                variant="body-small-bold"
              >
                Allocations ({item.allocations.length})
              </TrueImpactText>
              {item.allocations.length === 0 ? (
                <TrueImpactText variant="body-small" color="grayDark">
                  No allocations.
                </TrueImpactText>
              ) : (
                <MuiTable size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="right">Allocation ID</TableCell>
                      <TableCell align="right">Report ID</TableCell>
                      <TableCell>NPO Email</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Report</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {item.allocations.map(a => {
                      const disabled = a.reportEnabled === false;
                      const rowStyle: React.CSSProperties = disabled
                        ? {
                            color: COLORS.grayDark,
                            textDecoration: 'line-through',
                            opacity: 0.7,
                          }
                        : {};
                      return (
                        <TableRow key={a.allocationId} style={rowStyle}>
                          <TableCell
                            className="mono"
                            align="right"
                            style={rowStyle}
                          >
                            {a.allocationId}
                          </TableCell>
                          <TableCell
                            className="mono"
                            align="right"
                            style={rowStyle}
                          >
                            {a.reportId ?? '—'}
                          </TableCell>
                          <TableCell className="mono" style={rowStyle}>
                            {a.npoEmail || '—'}
                          </TableCell>
                          <TableCell align="right" style={rowStyle}>
                            {formatMoney(a.amount)}
                          </TableCell>
                          <TableCell>
                            {disabled ? (
                              <StatusPill tone="warn">
                                Disabled report
                              </StatusPill>
                            ) : (
                              <StatusPill tone="ok">Enabled</StatusPill>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </MuiTable>
              )}

              <TrueImpactText
                className="section-title"
                variant="body-small-bold"
                style={{ marginTop: SPACERS.small }}
              >
                Invitations ({item.invitations.length})
              </TrueImpactText>
              {item.invitations.length === 0 ? (
                <TrueImpactText variant="body-small" color="grayDark">
                  No invitations.
                </TrueImpactText>
              ) : (
                <MuiTable size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="right">Invitation ID</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Notification Sent</TableCell>
                      <TableCell>Email Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {item.invitations.map(inv => (
                      <TableRow key={inv.invitationId}>
                        <TableCell className="mono" align="right">
                          {inv.invitationId}
                        </TableCell>
                        <TableCell className="mono">
                          {inv.email || '—'}
                        </TableCell>
                        <TableCell>
                          <StatusPill tone={statusTone(inv.investmentStatus)}>
                            {inv.investmentStatus || 'unknown'}
                          </StatusPill>
                        </TableCell>
                        <TableCell>
                          {inv.notificationDt ? (
                            formatDate(inv.notificationDt)
                          ) : (
                            <StatusPill tone="muted">Not sent</StatusPill>
                          )}
                        </TableCell>
                        <TableCell
                          className="mono"
                          style={{
                            maxWidth: 320,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={inv.emailStatus || ''}
                        >
                          {inv.emailStatus || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </MuiTable>
              )}
            </NestedSection>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const OverAllocatedInvestments: React.FC = () => {
  const client = useClient();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [minAllocationsInput, setMinAllocationsInput] = useState('');
  const [excludes, setExcludes] = useState<string[]>([]);
  const [chipDraft, setChipDraft] = useState('');
  const [onlyOverAllocated, setOnlyOverAllocated] = useState(true);
  const [onlyEnabledReports, setOnlyEnabledReports] = useState(true);
  const [funderEmailInput, setFunderEmailInput] = useState('');
  const [funderEmailContains, setFunderEmailContains] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<{
    minAllocations: number | null;
    excludeNameContains: string[];
  }>({ minAllocations: null, excludeNameContains: [] });

  const [items, setItems] = useState<OverAllocatedInvestmentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await getOverAllocatedInvestments(client, {
        page,
        perPage,
        minAllocations: appliedFilters.minAllocations ?? undefined,
        excludeNameContains:
          appliedFilters.excludeNameContains.length > 0
            ? appliedFilters.excludeNameContains
            : undefined,
        onlyOverAllocated,
        onlyEnabledReports,
        funderEmailContains: funderEmailContains || undefined,
      });
      const payload = data?.overAllocatedInvestments;
      setItems(payload?.items || []);
      setTotal(payload?.total || 0);
    } catch (err) {
      setError(formatGqlError(err));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    client,
    page,
    perPage,
    appliedFilters,
    onlyOverAllocated,
    onlyEnabledReports,
    funderEmailContains,
  ]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setFunderEmailContains(prev => {
        if (prev !== funderEmailInput) {
          setPage(1);
          return funderEmailInput;
        }
        return prev;
      });
    }, 300);
    return () => clearTimeout(handle);
  }, [funderEmailInput]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addChip = (raw: string) => {
    const v = raw.trim();
    if (!v) return;
    if (excludes.includes(v)) {
      setChipDraft('');
      return;
    }
    setExcludes(prev => [...prev, v]);
    setChipDraft('');
  };

  const removeChip = (v: string) => {
    setExcludes(prev => prev.filter(x => x !== v));
  };

  const applyFilters = () => {
    const parsed = parseInt(minAllocationsInput, 10);
    setPage(1);
    setAppliedFilters({
      minAllocations: isNaN(parsed) ? null : parsed,
      excludeNameContains: excludes,
    });
  };

  const clearFilters = () => {
    setMinAllocationsInput('');
    setExcludes([]);
    setChipDraft('');
    setFunderEmailInput('');
    setFunderEmailContains('');
    setOnlyOverAllocated(false);
    setOnlyEnabledReports(true);
    setPage(1);
    setAppliedFilters({ minAllocations: null, excludeNameContains: [] });
  };

  const handleChipKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      if (chipDraft.trim()) {
        e.preventDefault();
        addChip(chipDraft);
      }
    } else if (e.key === 'Backspace' && !chipDraft && excludes.length > 0) {
      removeChip(excludes[excludes.length - 1]);
    }
  };

  return (
    <PageWrapper>
      <PageHeader>
        <div className="header-icon">
          <FontAwesomeIcon icon={faScaleUnbalanced} size="lg" />
        </div>
        <div>
          <TrueImpactText variant="heading-page" className="heading">
            Investments Allocation Report
          </TrueImpactText>
          <TrueImpactText className="subheading">
            All investments with their allocation totals. Use the filters to
            surface over-allocated rows or to include allocations from disabled
            reports.
          </TrueImpactText>
        </div>
      </PageHeader>

      <Card>
        <TrueImpactText variant="heading-subheading">Filters</TrueImpactText>
        <FiltersRow>
          <div>
            <TrueImpactText className="field-label" variant="body-small-bold">
              Min allocations
            </TrueImpactText>
            <TrueImpactTextfield
              placeholder="e.g. 2"
              type="number"
              value={minAllocationsInput}
              onChange={v => setMinAllocationsInput(v)}
            />
          </div>
          <div>
            <TrueImpactText className="field-label" variant="body-small-bold">
              Exclude funder name contains
            </TrueImpactText>
            <ChipsField>
              {excludes.map(v => (
                <Chip key={v}>
                  {v}
                  <span
                    className="remove"
                    onClick={() => removeChip(v)}
                    title="Remove"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </span>
                </Chip>
              ))}
              <input
                placeholder={
                  excludes.length === 0
                    ? 'Type and press Enter (e.g. test, example)'
                    : 'Add another…'
                }
                value={chipDraft}
                onChange={e => setChipDraft(e.target.value)}
                onKeyDown={handleChipKeyDown}
                onBlur={() => chipDraft.trim() && addChip(chipDraft)}
              />
            </ChipsField>
          </div>
          <div style={{ display: 'flex', gap: SPACERS.tiny }}>
            <TrueImpactButton variant="primary-small" onClick={applyFilters}>
              Apply
            </TrueImpactButton>
            <TrueImpactButton variant="secondary-small" onClick={clearFilters}>
              Clear
            </TrueImpactButton>
          </div>
        </FiltersRow>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: SPACERS.medium,
            marginTop: SPACERS.small,
          }}
        >
          <TrueImpactCheckbox
            checked={onlyOverAllocated}
            text="Only over-allocated"
            handleCheck={checked => {
              setPage(1);
              setOnlyOverAllocated(checked);
            }}
          />
          <TrueImpactCheckbox
            checked={onlyEnabledReports}
            text="Only count enabled reports"
            handleCheck={checked => {
              setPage(1);
              setOnlyEnabledReports(checked);
            }}
          />
          <div style={{ flex: 1, minWidth: 240 }}>
            <TrueImpactTextfield
              placeholder="Funder email contains…"
              value={funderEmailInput}
              onChange={v => setFunderEmailInput(v)}
            />
          </div>
        </div>
      </Card>

      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: SPACERS.small,
          }}
        >
          <TrueImpactText variant="heading-subheading">Results</TrueImpactText>
          <TrueImpactText variant="body-small" color="grayDark">
            {total} investment{total === 1 ? '' : 's'}
          </TrueImpactText>
        </div>

        {error && <ErrorBanner>{error}</ErrorBanner>}

        {loading && items.length === 0 ? (
          <LoadingWrapper>
            <TrueImpactLoading />
          </LoadingWrapper>
        ) : items.length === 0 ? (
          <EmptyState>
            <FontAwesomeIcon icon={faScaleUnbalanced} className="icon" />
            <TrueImpactText>
              No over-allocated investments match the current filters.
            </TrueImpactText>
          </EmptyState>
        ) : (
          <StyledTableContainer
            style={{ opacity: loading ? 0.6 : 1, transition: '0.2s' }}
          >
            <MuiTable size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell align="right">Investment ID</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Funder</TableCell>
                  <TableCell>Funder Email</TableCell>
                  <TableCell>Grantee Account</TableCell>
                  <TableCell>Program Name By Donor</TableCell>
                  <TableCell align="right">Total Investment</TableCell>
                  <TableCell align="right">Total Allocated</TableCell>
                  <TableCell align="right">Over By</TableCell>
                  <TableCell align="right"># Allocations</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(item => (
                  <InvestmentRow key={item.investmentId} item={item} />
                ))}
              </TableBody>
            </MuiTable>
          </StyledTableContainer>
        )}

        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          onPageChange={(_e, newPage) => setPage(newPage + 1)}
          rowsPerPage={perPage}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onRowsPerPageChange={e => {
            setPage(1);
            setPerPage(parseInt(e.target.value, 10));
          }}
        />
      </Card>
    </PageWrapper>
  );
};

export default OverAllocatedInvestments;
